import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReceiptsV2 } from '~/actions';
import { shouldUseDsoStructure } from '~/growthbook';
import { useParentOffice } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useUserSelector } from '~/hooks/useUserSelector';
import { SortFields } from '../enums';

interface IProps {
  filters: IFilter;
}
interface IFilter {
  transactionDate: string;
}

export const useAllTransactionReceiptV2 = ({ filters }: IProps) => {
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const user = useUserSelector();

  const isDSOStructureEnabled = useRef(shouldUseDsoStructure()).current;

  const offices = useAppSelector((state) => state.user.offices?.map((office) => office.id));
  const childOffices = useAppSelector((state) =>
    state.user.childOffices?.map((office) => office.id),
  );
  const { data, metadata } = useAppSelector((state) => state.receipt.receipts);

  const { isParentOffice } = useParentOffice();

  const officeIds = useMemo(() => {
    if (!isDSOStructureEnabled && !isParentOffice) {
      return [user?.id];
    }

    if (!isDSOStructureEnabled) {
      return [...childOffices];
    }

    return offices;
  }, [childOffices, isDSOStructureEnabled, isParentOffice, offices, user?.id]);

  const [sort, setSort] = useState({
    field: SortFields.TransactionDate,
    order: 'desc',
  });

  const onFetchNextPage = () => {
    const { isFinal, page } = metadata;

    if (isFinal) {
      return;
    }

    dispatch(
      fetchReceiptsV2({
        limit: 25,
        officeId: officeIds,
        page: page + 1,
        sortBy: sort.field,
        sortOrder: sort.order,
        transactionDate: filters.transactionDate,
      }),
    );
  };

  const onSortChange = (field: SortFields) => {
    setSort((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    dispatch(
      fetchReceiptsV2({
        limit: 25,
        officeId: officeIds,
        page: 1,
        sortBy: sort.field,
        sortOrder: sort.order,
        transactionDate: filters.transactionDate,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sort, filters.transactionDate]);

  return {
    receipts: data,
    scrollContainerRef,
    sort,
    onFetchNextPage,
    onSortChange,
  };
};
